import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Upload, notification, Spin } from "antd";
import {
  LuMail,
  LuX,
  LuUserSquare,
} from "react-icons/lu";
import { HiOutlineLockClosed, HiOutlinePhoto } from "react-icons/hi2";
import {
  useCreateInterpreterMutation,
  useUpdateInterpreterMutation,
  useDeleteInterpreterMutation,
} from "@/store/api/apiSlice";
import { useUpdateInterpreterStatusMutation } from "../../store/api/apiSlice";

const AddInterpreterModal = ({ visible, onClose, editData = null }) => {
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const [imageUrl, setImageUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  const [isStatusUpdating, setIsStatusUpdating] = useState(false);

  const [createInterpreter, { isLoading: isCreating }] =
    useCreateInterpreterMutation();
  const [updateInterpreter, { isLoading: isUpdating }] =
    useUpdateInterpreterMutation();
  const [deleteInterpreter, { isLoading: isDeleting }] =
    useDeleteInterpreterMutation();
  const [updateInterpreterStatus] = useUpdateInterpreterStatusMutation();

  useEffect(() => {
    if (!visible) {
      setFileList([]);
      setImageUrl("");
      
      return;
    }

    if (editData) {
      form.setFieldsValue({
        fullName: editData.interpreter?.name,
        email: editData.interpreter?.email,
        experience: Number(editData.experience),
      });

      if (editData.interpreter?.avatar) {
        setImageUrl(editData.interpreter.avatar);
        setFileList([
          {
            uid: "-1",
            name: "Current Photo",
            status: "done",
            url: editData.interpreter.avatar,
          },
        ]);
      } else {
        setFileList([]);
        setImageUrl("");
      }
     
      return;
    }

    form.resetFields();
    setFileList([]);
    setImageUrl("");
  }, [editData, visible, form]);

  const handleStatusChange = async (status) => {
    try {
      setIsStatusUpdating(true);
      // debugger

      // Update local state immediately
      editData.status = status;

      await updateInterpreterStatus({
        id: editData._id,
        status: status, // Boolean value
      }).unwrap();

      notification.success({
        message: "Success",
        description: `Interpreter ${
          status === "deactivate" ? "deactivated" : "activated"
        } successfully`,
      });
    } catch (error) {
      // editData.is_deactivated = !status;
      notification.error({
        message: "Error",
        description: "Failed to update interpreter status",
      });
    } finally {
      setIsStatusUpdating(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteInterpreter(editData._id).unwrap();
      // Close the delete confirmation modal first
      setDeleteConfirmVisible(false);
      // Then close the main modal and cleanup
      onClose();
      // Show success notification
      notification.success({
        message: "Success",
        description: "Interpreter deleted successfully",
      });
    } catch (error) {
      setDeleteConfirmVisible(false); // Close delete modal even on error
      notification.error({
        message: "Error",
        description: "Failed to delete interpreter",
      });
    }
  };

  const handleSubmit = async (values) => {
    try {
      if (editData?._id) {
        // Create update payload with all form values
        const updatePayload = {
          // interpreter: {
          experience: parseInt(values.experience),

          // },
          user: {
            full_name: values.fullName,
          },
        };

        // Only add image if it was updated
        if (imageUrl && imageUrl !== editData.avatar) {
          updatePayload.image = imageUrl;
        }
        await updateInterpreter({
          id: editData._id,
          data: updatePayload,
        }).unwrap();

        notification.success({
          message: "Success",
          description: "Interpreter updated successfully!",
        });
        onClose();
      } else {
        // Create new interpreter
        const createPayload = {
          user: {
            full_name: values.fullName,
            email: values.email,
            password: values.password,
          },
          interpreter: {
            experience: parseInt(values.experience),
            image: imageUrl,
          },
        };
        await createInterpreter(createPayload).unwrap();
        notification.success({
          message: "Success",
          description: "Interpreter added successfully!",
        });
        onClose();
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description:
          error.data?.message ||
          `Failed to ${editData ? "update" : "create"} interpreter`,
      });
    }
  };

  const handleFileUpload = async (file) => {
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/file/create`,
        {
          method: "POST",
          body: formData,
        },
      );

      const data = await response.json();

      if (data.file) {
        setImageUrl(data.file);
        setFileList([
          {
            uid: "-1",
            name: file.name,
            status: "done",
            url: data.file,
          },
        ]);
        return data.file;
      }
      throw new Error("Upload failed");
    } catch (error) {
      notification.error({
        message: "Upload Failed",
        description: "Failed to upload photo. Please try again.",
      });
      return null;
    } finally {
      setUploading(false);
    }
  };
  const handleRemove = () => {
    setFileList([]);
    setImageUrl("");
    form.setFieldsValue({ photo: undefined });
    return true; // Return true to remove the file from the file list
  };
  const uploadProps = {
    accept: "image/*",
    beforeUpload: async (file) => {
      const isImage = file.type.startsWith("image/");
      if (!isImage) {
        notification.error({
          message: "Upload Error",
          description: "You can only upload image files!",
        });
        return false;
      }

      // const isLt2M = file.size / 1024 / 1024 < 3;
      // if (!isLt2M) {
      //   notification.error({
      //     message: 'Upload Error',
      //     description: 'Image must be smaller than 3MB!'
      //   });
      //   return false;
      // }

      await handleFileUpload(file);
      return false;
    },
    onRemove: handleRemove,
    fileList,
  };

  // Confirmation Modal for Delete
  const DeleteConfirmModal = ({ visible, onCancel, onConfirm, loading }) => (
    <Modal
      title="Delete Interpreter"
      open={visible}
      onCancel={() => {
        onCancel();
        // Ensure modal state is clean
        setDeleteConfirmVisible(false);
      }}
      okText="Delete"
      cancelText="Cancel"
      okButtonProps={{
        danger: true,
        loading: loading,
      }}
      onOk={onConfirm}
      destroyOnClose={true} // Add this to ensure modal is fully destroyed when closed
    >
      <p>
        Are you sure you want to delete this interpreter? This action cannot be
        undone.
      </p>
    </Modal>
  );

  return (
    <>
      <Modal
        open={visible}
        onCancel={onClose}
        footer={null}
        width={560}
        centered
        closeIcon={
          <div className="bg-secondary h-8 w-8 flex justify-center items-center rounded-full transition-colors hover:bg-gray-200">
            <LuX className="text-red-400" size={20} />
          </div>
        }
        title={editData ? "Edit Interpreter" : "Add Interpreter"}
        className="interpreter-modal [&_.ant-modal]:overflow-hidden [&_.ant-modal-content]:overflow-hidden [&_.ant-modal-close]:!rounded-full [&_.ant-modal-close]:overflow-hidden [&_.ant-modal-close:hover]:!rounded-full [&_.ant-modal-close:hover]:!bg-transparent"
        styles={{ body: { overflow: "hidden" } }}
        destroyOnClose
      >
        {editData && (
          <div className="flex justify-end gap-2 mb-4 shrink-0">
            <div className="flex bg-secondary rounded-xl p-1.5">
              <button
                type="button"
                onClick={() => handleStatusChange("active")}
                className={`h-10 px-5 border-0 rounded-xl transition-colors cursor-pointer ${
                  editData.status === "active"
                    ? "bg-yellow text-black"
                    : "text-gray-700 hover:bg-gray-100"
                }`}
                disabled={editData.status === "active"}
              >
                Activate
              </button>
              <button
                type="button"
                onClick={() => handleStatusChange("deactivate")}
                className={`h-10 px-5 border-0 rounded-xl transition-colors cursor-pointer ${
                  editData.status === "deactivate"
                    ? "bg-red-500 text-white"
                    : "text-gray-700 hover:bg-gray-100"
                }`}
                disabled={editData.status === "deactivate"}
              >
                Deactivate
              </button>
            </div>
          </div>
        )}
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          className="flex max-h-[calc(100vh-140px)] flex-col"
        >
          <div className="min-h-0 flex-1 overflow-y-auto pr-1">
            <div className="space-y-6">
            {/* Full Name - Always shown and editable */}
            <Form.Item
              label="Full Name"
              name="fullName"
              rules={[{ required: true }]}
            >
              <Input
                prefix={<LuUserSquare className="text-gray-400" />}
                placeholder="Please enter your full name..."
                className="h-12 rounded-2xl"
              />
            </Form.Item>

            {/* Email - Shown but disabled in edit mode */}
            <Form.Item
              label="Email Address"
              name="email"
              rules={[{ required: true, type: "email" }]}
            >
              <Input
                prefix={<LuMail className="text-gray-400" />}
                placeholder="Please enter your email address here..."
                className="h-12 rounded-2xl"
                disabled={!!editData}
              />
            </Form.Item>

            <Form.Item
              label="How many years of experience do you have?"
              name="experience"
              rules={[
                { required: true, message: "Experience is required" },
                {
                  validator: (_, value) => {
                    if (value && !isNaN(value) && parseInt(value) >= 0) {
                      return Promise.resolve();
                    }
                    return Promise.reject("Please enter a valid number");
                  },
                },
              ]}
            >
              <Input
                type="number"
                min={0}
                placeholder="Type a number..."
                className="h-12 rounded-2xl"
                onChange={(e) => {
                  const value = e.target.value;
                  if (value && !isNaN(value) && parseInt(value) >= 0) {
                    form.setFieldValue("experience", parseInt(value));
                  }
                }}
              />
            </Form.Item>

            {/* Upload Photo */}
            <Form.Item label="Upload Your Photo" name="photo">
              <Upload {...uploadProps} maxCount={1} className="">
                <div className="flex flex-col items-center ">
                  <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                    <HiOutlinePhoto className="text-gray-600" size={20} />
                  </div>
                  <span className="text-sm text-gray-600">
                    Upload a photo of yourself...
                  </span>
                </div>
              </Upload>
            </Form.Item>

            {/* Password fields - Only shown in create mode */}
            {!editData && (
              <>
                <Form.Item
                  label="Password"
                  name="password"
                  rules={[{ required: true, min: 8 }]}
                >
                  <Input.Password
                    prefix={<HiOutlineLockClosed className="text-gray-400" />}
                    placeholder="Minimum 8 characters..."
                    className="h-12 rounded-2xl"
                  />
                </Form.Item>

                <Form.Item
                  label="Confirm Password"
                  name="confirmPassword"
                  dependencies={["password"]}
                  rules={[
                    { required: true },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue("password") === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject("Passwords do not match!");
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    prefix={<HiOutlineLockClosed className="text-gray-400" />}
                    placeholder="Minimum 8 characters..."
                    className="h-12 rounded-2xl"
                  />
                </Form.Item>
              </>
            )}
            </div>
          </div>

          <div className="mt-8 shrink-0 items-center gap-3 flex">
            {editData && (
              <button
                type="button"
                onClick={() => setDeleteConfirmVisible(true)}
                className="w-full h-12 bg-red-50 text-red-500 rounded-2xl border-0 font-medium flex items-center justify-center gap-2"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <path
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Delete Interpreter Profile
              </button>
            )}

            <button
              type="submit"
              className="w-full h-12 bg-[#18181B] text-white rounded-2xl border-0 font-medium mt-0"
              disabled={isCreating || isUpdating || uploading}
            >
              {isCreating || isUpdating ? (
                <Spin size="small" />
              ) : editData ? (
                "Update Interpreter Profile"
              ) : (
                "Add Interpreter"
              )}
            </button>
          </div>
        </Form>
      </Modal>

      <DeleteConfirmModal
        visible={deleteConfirmVisible}
        onCancel={() => setDeleteConfirmVisible(false)}
        onConfirm={handleDelete}
        loading={isDeleting}
      />
    </>
  );
};

export default AddInterpreterModal;
